import {
    CaretSortIcon,
    CheckCircledIcon,
    CheckIcon,
    FontBoldIcon,
    FontItalicIcon,
    Link2Icon,
    ListBulletIcon,
    UnderlineIcon,
    UpdateIcon,
} from '@radix-ui/react-icons';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import {
    createButton,
    createDropdown,
    Editor,
    EditorProvider,
    Toolbar,
} from 'react-simple-wysiwyg';
import '../App.css';
import { DatePickerSingle } from '../components/datePickerSingle';
import { Button } from '../components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../components/ui/command";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Input } from '../components/ui/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../components/ui/popover';
import { toast } from '../components/ui/use-toast';
import { AuthContext } from '../context/AuthContext';
import api from '../lib/api';
import { cn } from '../lib/utils';
import DOMPurify from 'dompurify';
export const BtnBulletList = createButton(
    'Bullet list',
    <ListBulletIcon />,
    'insertUnorderedList',
);
export const BtnItalic = createButton('Italic', <FontItalicIcon />, 'italic');
export const BtnBold = createButton('Bold', <FontBoldIcon />, 'bold')
export const BtnUnderline = createButton('Underline', <UnderlineIcon />, 'underline')
export const BtnLink = createButton('Link', <Link2Icon />, ({ $selection }) => {
    if ($selection?.nodeName === 'A') {
        document.execCommand('unlink');
    } else {
        // eslint-disable-next-line no-alert
        document.execCommand('createLink', false, prompt('URL', '') || undefined);
    }
});
export const BtnStyles = createDropdown('Stilar', [
    ['Normal', 'formatBlock', 'DIV'],
    ['𝐑𝐮𝐛𝐫𝐢𝐤 𝟏', 'formatBlock', 'H1'],
    ['Rubrik 2', 'formatBlock', 'H2'],
]);

function CompetitionInformation() {
    const [isLoading, setIsLoading] = useState(false);

    const form = useForm();
    const { setValue } = form;
    const { auth } = useContext(AuthContext);

    const [selectedClub, setSelectedClub] = useState(null);

    const [clubs, setClubs] = useState([]);
    const [clubsIsLoading, setClubsIsLoading] = useState();
    const [clubSelectOpen, setClubSelectOpen] = useState(false);

    const [formData, setFormData] = useState({
        clubId: auth.user?.memberOfClub,
        competitionName: '',
        startDate: '',
        endDate: '',
        registrationDate: '',
        lateRegistrationDate: '',
        description: '',
        entryPrice: '',
        lateEntryPrice: '',
        miniEntryPrice: '',
        oocEntryPrice: '',
        cupEntryPrice: '',
        classTestPrice: '',
    });
    const { competitionId } = useParams();

    const navigate = useNavigate();

    const fetchCompetition = useCallback(async () => {
        if (!competitionId) return;  // If there's no competitionId, exit early

        try {
            setIsLoading(true);
            const response = await api.get(`https://skhrf-api.cmdigital.se/v1/competitions/information/${competitionId}`);


            const competition = response.data;
            const sanitizedDescription = DOMPurify.sanitize(competition.description, {
                ALLOWED_TAGS: ['div', 'br', 'b', 'i', 'u', 'a', 'h1', 'h2'],
                ALLOWED_ATTR: ['href']
            });
            setFormData({
                clubId: competition.clubId,
                competitionName: competition.name,
                startDate: competition.start_date?.slice(0, 10),
                endDate: competition.end_date?.slice(0, 10),
                registrationDate: competition.registration_date?.slice(0, 10),
                lateRegistrationDate: competition.late_registration_date?.slice(0, 10),
                description: sanitizedDescription,
                entryPrice: competition.entry_price,
                lateEntryPrice: competition.late_entry_price,
                miniEntryPrice: competition.mini_entry_price,
                oocEntryPrice: competition.ooc_entry_price,
                cupEntryPrice: competition.cup_entry_price,
                classTestPrice: competition.class_test_price
            });
        } catch (error) {
            console.error('Error fetching competition:', error);
            if (error.response && error.response.status === 404) {
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel.',
                    description: 'Tävlingen du efterfrågade finns inte.',
                });
            } else {
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel.',
                    description: 'Ett oväntat fel inträffade.',
                });
            }
        } finally {
            setIsLoading(false);
        }
    }, [competitionId]);

    const fetchClubs = useCallback(() => {
        setClubsIsLoading(true);
        api.get('https://skhrf-api.cmdigital.se/v1/clubs/')
            .then(response => {
                const activeClubs = response.data.filter(club => !club.archived_at);
                setClubs(activeClubs);
                setClubsIsLoading(false);
            })
            .catch(error => {
                console.error('Error fetching clubs:', error);
                setClubsIsLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchCompetition();
    }, [fetchCompetition]);

    useEffect(() => {
        if (!competitionId) fetchClubs();
    }, [fetchClubs, competitionId]);

    const handleSubmitForm = async (e) => {
        e.preventDefault();
        if (isLoading) return;
        setIsLoading(true);

        try {
            const cupEntryPrice = formData.cupEntryPrice || 0;
            const classTestPrice = formData.classTestPrice || 0;

            // Sanitize the HTML from the editor before sending
            const sanitizedDescription = DOMPurify.sanitize(formData.description, {
                ALLOWED_TAGS: ['div', 'br', 'b', 'i', 'u', 'a', 'h1', 'h2'],
                ALLOWED_ATTR: ['href']
            });

            if (competitionId) {
                // Update existing competition (if competitionId exists)
                const response = await api.post(`https://skhrf-api.cmdigital.se/v1/competitions/edit`, {
                    competitionId,
                    competitionName: formData.competitionName,
                    startDate: formData.startDate,
                    endDate: formData.endDate,
                    registrationDate: formData.registrationDate,
                    lateRegistrationDate: formData.lateRegistrationDate,
                    description: sanitizedDescription,
                    entryPrice: formData.entryPrice,
                    lateEntryPrice: formData.lateEntryPrice,
                    miniEntryPrice: formData.miniEntryPrice,
                    oocEntryPrice: formData.oocEntryPrice,
                    cupEntryPrice: cupEntryPrice,
                    classTestPrice: classTestPrice,
                });


                toast({
                    variant: 'success',
                    title: 'Tävling uppdaterad!',
                    description: "Ändringarna har sparats.",
                });

                navigate(`/competition/${competitionId}`);
            } else {
                // Create new competition (if no competitionId exists)


                const response = await api.post(`https://skhrf-api.cmdigital.se/v1/competitions/create`, {
                    clubId: selectedClub || auth.user?.memberOfClub,
                    competitionName: formData.competitionName,
                    startDate: formData.startDate,
                    endDate: formData.endDate,
                    registrationDate: formData.registrationDate,
                    lateRegistrationDate: formData.lateRegistrationDate,
                    description: sanitizedDescription,
                    entryPrice: formData.entryPrice,
                    lateEntryPrice: formData.lateEntryPrice,
                    miniEntryPrice: formData.miniEntryPrice,
                    oocEntryPrice: formData.oocEntryPrice,
                    cupEntryPrice: cupEntryPrice,
                    classTestPrice: classTestPrice,
                });


                setFormData({
                    competitionName: '',
                    startDate: '',
                    endDate: '',
                    registrationDate: '',
                    lateRegistrationDate: '',
                    description: '',
                    entryPrice: '',
                    lateEntryPrice: '',
                    miniEntryPrice: '',
                    oocEntryPrice: '',
                    cupEntryPrice: '',
                    classTestPrice: '',
                });

                toast({
                    variant: 'success',
                    title: 'Tävling skapad!',
                    description: "Du skickas till tävlingen.",
                });

                navigate(`/competition/${response.data.createdCompetitionId}`);
            }
        } catch (error) {
            console.error('Error handling competition form submission:', error);
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        setValue(name, value);
    };

    const handleDateChange = (date, name) => {
        if (!date) {
            return; // Early return if the date is null or undefined
        }
        //const formattedDate = convertToUTCsend(date);

        setFormData(prevState => ({
            ...prevState,
            [name]: date.toLocaleString('sv').split(' ')[0], // Update the specific field (startDate, endDate, etc.)
        }));
    };

    return (
        <div className='flex w-full items-center justify-center'>
            <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>
                <h1 className='font-medium text-2xl mb-2'>
                    {competitionId ? 'Redigera tävling' : 'Skapa ny tävling'}
                </h1>
                <Form {...form} >
                    <form className="flex flex-col gap-6 w-full sm:w-1/2">
                        {(!competitionId && auth?.user?.superadmin && !clubsIsLoading) && (
                            <FormField
                                control={form.control}
                                name="clubName"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel>Förening</FormLabel>
                                        <Popover modal={true} open={clubSelectOpen} onOpenChange={setClubSelectOpen}>
                                            <PopoverTrigger asChild>
                                                <FormControl>
                                                    <Button
                                                        variant="outline"
                                                        role="combobox"
                                                        className={cn(
                                                            "w-full justify-between",
                                                            !field.value && "text-muted-foreground"
                                                        )}
                                                    >
                                                        {field.value
                                                            ? clubs.find(
                                                                (club) => club.id === field.value
                                                            )?.name
                                                            : "Välj förening"}
                                                        <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                    </Button>
                                                </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent className="popover-content-width-full p-0">
                                                <Command>
                                                    <CommandInput placeholder="Sök förening..." />
                                                    <CommandList>
                                                        <CommandEmpty>Ingen förening hittades.</CommandEmpty>
                                                        <CommandGroup>
                                                            {clubs.map((club) => (
                                                                <CommandItem
                                                                    className='cursor-pointer'
                                                                    value={club.id}
                                                                    key={club.id}
                                                                    onSelect={() => {
                                                                        setClubSelectOpen(false);
                                                                        form.setValue("clubName", club.id)
                                                                        setSelectedClub(club.id);
                                                                    }}
                                                                >
                                                                    <CheckIcon
                                                                        className={cn(
                                                                            "mr-2 h-4 w-4",
                                                                            club.id === field.value
                                                                                ? "opacity-100"
                                                                                : "opacity-0"
                                                                        )}
                                                                    />
                                                                    {club.name}
                                                                </CommandItem>
                                                            ))}
                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                        <FormDescription>
                                            Enbart förbundsadministratörer kan ange förening för tävling manuellt.
                                        </FormDescription>
                                        <FormMessage />

                                    </FormItem>
                                )}
                            />)}
                        <FormField
                            control={form.control}
                            name="competitionName"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel>Namn</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Stora tävlingen" name="competitionName" value={formData.competitionName} onChange={handleChange} disabled={isLoading} />
                                    </FormControl>
                                    <FormDescription>
                                        Namnet som visas på tävlingen.
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-3 items-start'>
                            <FormField
                                control={form.control}
                                name="startDate"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel htmlFor='startDate'>Startdatum</FormLabel>
                                        <FormControl>
                                            <DatePickerSingle
                                                id='startDate'
                                                onDateChange={(date) => handleDateChange(date, 'startDate')}
                                                selectedDate={
                                                    formData.startDate
                                                        ? new Date(`${formData.startDate}T00:00:00`)
                                                        : null
                                                }
                                                key={formData.startDate}
                                                disabled={isLoading}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Datumet tävlingen börjar.
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="endDate"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel htmlFor='endDate'>Slutdatum</FormLabel>
                                        <FormControl>
                                            <DatePickerSingle
                                                id='endDate'
                                                onDateChange={(date) => handleDateChange(date, 'endDate')}
                                                selectedDate={
                                                    formData.endDate
                                                        ? new Date(`${formData.endDate}T00:00:00`)
                                                        : null
                                                }
                                                key={formData.endDate}
                                                disabled={isLoading}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Pågår tävlingen en dag, sätt samma datum som startdatum.
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-3 items-start'>
                            <FormField
                                control={form.control}
                                name="registrationDate"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel htmlFor='registrationDate'>Sista anmälningsdag</FormLabel>
                                        <FormControl>
                                            <DatePickerSingle
                                                id='registrationDate'
                                                onDateChange={(date) => handleDateChange(date, 'registrationDate')}
                                                selectedDate={
                                                    formData.registrationDate
                                                        ? new Date(`${formData.registrationDate}T00:00:00`)
                                                        : null
                                                }
                                                key={formData.registrationDate}
                                                disabled={isLoading}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Sista dag för normal anmälan. Efter detta datum gäller efteranmälan.
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="lateRegistrationDate"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel htmlFor='lateRegistrationDate'>Sista efteranmälningsdag</FormLabel>
                                        <FormControl>
                                            <DatePickerSingle
                                                id='lateRegistrationDate'
                                                onDateChange={(date) => handleDateChange(date, 'lateRegistrationDate')}
                                                selectedDate={
                                                    formData.lateRegistrationDate
                                                        ? new Date(`${formData.lateRegistrationDate}T00:00:00`)
                                                        : null
                                                }
                                                key={formData.lateRegistrationDate}
                                                disabled={isLoading}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Ska tävlingen inte ha efteranmälan, sätt samma datum som sista anmälningsdag.
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel htmlFor='description' >Tävlingsinformation</FormLabel>
                                    <FormControl>
                                        <EditorProvider >
                                            <Editor
                                                id='description'
                                                disabled={isLoading}
                                                value={formData.description}
                                                onChange={(e) =>
                                                    handleChange({
                                                        target: { name: 'description', value: e.target.value },
                                                    })
                                                }>
                                                <Toolbar>
                                                    <BtnBold />
                                                    <BtnItalic />
                                                    <BtnUnderline />
                                                    <BtnBulletList />
                                                    <BtnLink />
                                                    <BtnStyles />
                                                </Toolbar>
                                            </Editor>
                                        </EditorProvider>
                                    </FormControl>
                                    <FormDescription>
                                        Här kan du ändra den information som visas om tävlingen.
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className='grid grid-cols-1 gap-3 items-start'>
                            <div>
                                <h2 className='font-medium text-lg'>Prisinformation</h2>
                                <p className='text-sm text-muted-foreground'>Ange priser för respektive starttyp i hela svenska kronor.</p>
                            </div>
                            <div className='grid grid-cols-2 gap-3 items-start'>
                                <FormField
                                    control={form.control}
                                    name="entryPrice"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel>Ordinarie start</FormLabel>
                                            <FormControl>
                                                <Input name='entryPrice' type='number' min='0' value={formData.entryPrice} onChange={handleChange} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="miniEntryPrice"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel>Mini start</FormLabel>
                                            <FormControl>
                                                <Input name='miniEntryPrice' type='number' min='0' value={formData.miniEntryPrice} onChange={handleChange} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className='grid grid-cols-2 gap-3 items-start'>
                                <FormField
                                    control={form.control}
                                    name="lateEntryPrice"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel>Efteranmälan</FormLabel>
                                            <FormControl>
                                                <Input name='lateEntryPrice' type='number' min='0' value={formData.lateEntryPrice} onChange={handleChange} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="oocEntryPrice"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel>Utom tävlan</FormLabel>
                                            <FormControl>
                                                <Input name='oocEntryPrice' type='number' min='0' value={formData.oocEntryPrice} onChange={handleChange} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className='grid grid-cols-2 gap-3 items-start'>
                                <FormField
                                    control={form.control}
                                    name="cupEntryPrice"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel>Cupanmälan</FormLabel>
                                            <FormControl>
                                                <Input name='cupEntryPrice' type='number' min='0' value={formData.cupEntryPrice} onChange={handleChange} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="classTestPrice"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel>Klassprövning</FormLabel>
                                            <FormControl>
                                                <Input name='classTestPrice' type='number' min='0' value={formData.classTestPrice} onChange={handleChange} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                        <Button type="submit" disabled={isLoading} onClick={handleSubmitForm}>
                            {isLoading ? <UpdateIcon className='loader-spinner' /> : <><CheckCircledIcon className='mr-3' />{competitionId ? 'Uppdatera' : 'Skapa'}</>}
                        </Button>
                    </form>
                </Form>
            </div>
        </div>
    );
}

export default CompetitionInformation;